<template>
    <div class="AdminEntityItemDetail">
        <FadeTransition mode="out-in">
            <div class="AdminEntityItemHeader flex row-stretch-center">
                <Breadcrumbs :breadcrumbs="breadcrumbs" />
                <div v-if="buttons">
                    <WButton
                        v-for="({ text, disabled, onClick, ...otherProps }, index) in buttons"
                        :key="index"
                        :disabled="disabled"
                        v-bind="otherProps"
                        class="ml-1"
                        @click="onClick"
                    >
                        {{ text }}
                    </WButton>
                </div>
            </div>
        </FadeTransition>

        <WCard :loading="isLoading">
            <EntityItemDetail
                :value="row"
                :columns="columns"
                :selectGroups="selectGroups"
                :entityKey="entityKey"
                :loading="isLoading"
            >
                <template v-if="(resources || []).length">
                    <ResourcesItem
                        v-for="(resource, index) in resources"
                        :key="`${resource.entityKey}-${index}`"
                        v-bind="resource"
                        :views="isAdmin"
                        class="mb-2"
                    />
                </template>
            </EntityItemDetail>
        </WCard>

        <ModalRequest
            :opened="modal === modals.DELETE"
            :title="$t('entities.actionsModals.deleteSelection.text')"
            :cancelText="$t('actions.cancel')"
            :okText="$t('actions.confirm')"
            :loading="isLoading"
            preventDefault
            @close="handleCleanModal"
            @onAccept="removeItem"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import {
    Api, apiRequest, rethrowError, notifyHttpError, notifySuccess, notifyError,
} from '@/api'
import { Breadcrumbs, ModalRequest, EntityItemDetail } from '@/components'
import { ENTITY_TYPES } from '@/entities/entities'
import { INPUTS, FIELD_INPUTS } from '@/entities/fields'
import { ResourcesItem } from './components'

const MODAL_TYPES = {
    DELETE: 'DELETE',
}

const RESOURCE_TYPES = {
    GRID: 'GRID',
    DETAIL: 'DETAIL',
}

const RESOURCES = {
    [ENTITY_TYPES.GPS]: [
        { type: RESOURCE_TYPES.DETAIL, key: 'funds', detailKey: ENTITY_TYPES.FUNDS }],
    [ENTITY_TYPES.FUNDS]: [
        { type: RESOURCE_TYPES.DETAIL, key: 'performance', detailKey: ENTITY_TYPES.FUNDS_PERF },
        { type: RESOURCE_TYPES.DETAIL, key: 'transactions', detailKey: ENTITY_TYPES.TRANSACTIONS },
        {
            type: RESOURCE_TYPES.GRID, key: 'trans', detailKey: ENTITY_TYPES.TRANSACTIONS, props: { editable: true },
        },
    ],
    [ENTITY_TYPES.LPS]: [
        { type: RESOURCE_TYPES.DETAIL, key: 'investments', detailKey: ENTITY_TYPES.LPS_INVESTMENTS }],
    [ENTITY_TYPES.LPS_INVESTMENTS]: [
        { type: RESOURCE_TYPES.DETAIL, key: 'secondaryAsBuyer', detailKey: ENTITY_TYPES.LPS_SECONDARY_TRANS },
        { type: RESOURCE_TYPES.DETAIL, key: 'secondaryAsSeller', detailKey: ENTITY_TYPES.LPS_SECONDARY_TRANS },
    ],
}

export default {
    name: 'AdminEntityItemDetail',
    components: {
        Breadcrumbs,
        ModalRequest,
        EntityItemDetail,
        ResourcesItem,
    },
    metaInfo () {
        return {
            title: this.$t(`admin.navigation.entities.${this.entityKey}`),
        }
    },
    data () {
        return {
            meta: undefined,
            row: undefined,
            columns: undefined,
            selectGroups: undefined,
            isLoading: false,

            // Resources
            resources: undefined,

            // Modals
            modal: undefined,
            modals: MODAL_TYPES,

            buttons: [
                // {
                //     iconLeft: 'trash',
                //     text: this.$t('entities.actions.delete'),
                //     outline: true,
                //     onClick: () => (this.modal = this.modals.DELETE),
                // },
                {
                    iconLeft: 'edit',
                    text: this.$t('entities.actions.edit'),
                    onClick: () => this.$router.push({ name: 'AdminEntityItemEdit', params: { entityKey: this.entityKey, id: this.id } }),
                },
            ],
        }
    },
    computed: {
        ...mapGetters({
            isAdmin: 'auth/isAdmin',
        }),
        breadcrumbs () {
            const item = this.row || {}
            const { nameField: field, nameFieldType: type, parent } = this.meta || {}
            const PARENT_DEFAULT = {
                key: this.entityKey,
                label: this.$t(`admin.navigation.entities.${this.entityKey}`),
                to: { name: 'AdminEntityList', params: { entityKey: this.entityKey } },
            }

            return [...(parent ? [{
                key: parent.entity,
                label: this.$t(`admin.navigation.entities.${parent.entity}`),
                to: { name: 'AdminEntityList', params: { entityKey: parent.entity } },
            }, {
                key: `${parent.entity}-detail`,
                label: this.normalizeMeta({ value: item[parent.nameField], type: parent.nameFieldType }),
                to: { name: 'AdminEntityItemDetail', params: { entityKey: parent.entity, id: item[parent.idField] } },
            }, PARENT_DEFAULT] : [PARENT_DEFAULT]), {
                key: 'entity-detail',
                label: this.normalizeMeta({ value: item[field], type }),
            }]
        },
    },
    beforeCreate () {
        const { entityKey, id } = this.$route.params
        this.entityKey = entityKey
        this.id = id
    },
    mounted () {
        this.getData()
        this.getResources()
    },
    methods: {
        normalizeMeta ({ value, type }) {
            if (FIELD_INPUTS[type] === INPUTS.TEXT || !type) return value || ''
            if (FIELD_INPUTS[type] === INPUTS.DATE) return moment(value).format('DD/MM/YYYY')
            return ''
        },
        handleCleanModal () {
            this.modal = undefined
        },
        async getData () {
            this.isLoading = true
            apiRequest(Api().outputs.get({ entityKey: this.entityKey, params: { id: this.id } }))
                .then(({ meta, data }) => {
                    const { selectGroups, columns, rows } = data || {}
                    const [row] = rows || []
                    this.meta = meta
                    this.row = row
                    this.selectGroups = selectGroups
                    this.columns = columns
                })
                .catch((error) => {
                    this.$router.back()
                    rethrowError(notifyHttpError())(error)
                })
                .finally(() => (this.isLoading = false))
        },
        async removeItem () {
            this.isLoading = true
            apiRequest(Api().outputs.delete({ entityKey: this.entityKey, id: this.id }))
                .then((response) => {
                    notifySuccess(this.$t('entities.modals.removeSuccess'))()
                    this.$router.back()
                })
                .catch(rethrowError(notifyError()))
                .finally(() => {
                    this.handleCleanModal()
                    this.isLoading = false
                })
        },
        async getResources () {
            const resources = RESOURCES[this.entityKey]
            if (!resources) return

            Promise.all(resources.map(({ type, key }) => {
                if (RESOURCE_TYPES.GRID === type) return Api().outputs.get({ entityKey: key, params: { fundId: this.id } })
                return Api().outputs.detail({ entityKey: this.entityKey, id: this.id, resource: key })
            }))
                .then((response) => {
                    response.forEach(({ ok, data }, index) => {
                        if (ok) {
                            const { key, detailKey, props = {} } = resources[index]
                            this.resources = [...(this.resources || []), {
                                entityKey: this.entityKey,
                                type: key,
                                detailKey,
                                ...props,
                                ...data.data,
                            }]
                        }
                    })
                }).catch(rethrowError(notifyHttpError()))
        },
    },
}
</script>
<style lang="scss" scoped>
.AdminEntityItemHeader {
    margin-bottom: 20px;
}

.WCard {
	@include admin-screen-height(54px);
}
</style>
